// Drop Zone v2 — detection core (spec #761, §B.2).
//
// Pure and node-free: this is the filename-only pass (Layer 1). The browser
// preview imports `detectFromEntries` directly; the CLI/server reach it through
// `detectArtifact` (detect.ts), which adds the folder/Layer-2 read on top.

import type {
  DetectionResult,
  DetectionSignal,
  DropEntry,
  Confidence,
  DetectedKind,
} from './types.js';
import { filterNoise } from './noise.js';
import { findAppSignals } from './heuristics/app.js';
import { findInspirationSignals } from './heuristics/inspiration.js';
import { deriveSlug, isGenericName } from './heuristics/slug.js';
import { deriveName } from './heuristics/name.js';

/** Map the winning signal's weight to a confidence band. */
function bandFor(weight: number): Confidence {
  if (weight >= 0.8) return 'high';
  if (weight >= 0.5) return 'medium';
  return 'low';
}

function maxWeight(signals: DetectionSignal[]): number {
  return signals.reduce((m, s) => (s.weight > m ? s.weight : m), 0);
}

/**
 * Classify a dropped artifact from its file list alone (filenames + sizes).
 *
 * Implements the spec's decision tree (§B.2) in order:
 *   1. any app run manifest → app, high (the app signal wins over co-present
 *      inspiration files — the ambiguity rule).
 *   2. a single inspiration file, or a bundle of only inspiration files →
 *      inspiration, high.
 *   3. an HTML bundle with assets but no manifest → inspiration, medium.
 *   4. otherwise → unknown, low, with the two most likely kinds for the
 *      forced "App or inspiration?" question.
 */
export function detectFromEntries(
  rawEntries: DropEntry[],
  topLevelName: string,
): DetectionResult {
  const files = filterNoise(rawEntries);

  const app = findAppSignals(files);
  const insp = findInspirationSignals(files);

  const suggested_slug = deriveSlug(topLevelName);
  const suggested_name = deriveName(topLevelName);
  const genericSignal: DetectionSignal[] = isGenericName(topLevelName)
    ? [{ marker: 'generic-name', weight: 0, contributes_to: 'unknown' }]
    : [];

  // 1 — App wins on any run manifest.
  if (app.isApp) {
    const signals = [...app.signals, ...genericSignal];
    return {
      kind: 'app',
      confidence: bandFor(maxWeight(app.signals)),
      signals,
      suggested_slug,
      suggested_name,
      app_framework: app.framework,
    };
  }

  // 2 — A single inspiration file or an all-inspiration bundle.
  const strongInsp = insp.signals.filter(
    (s) => s.marker === 'single-inspiration-file' || s.marker === 'all-files-inspiration',
  );
  if (strongInsp.length > 0) {
    const signals = [...insp.signals, ...genericSignal];
    return {
      kind: 'inspiration',
      confidence: bandFor(maxWeight(strongInsp)),
      signals,
      suggested_slug,
      suggested_name,
      inspiration_subtype: insp.subtype,
    };
  }

  // 3 — Static HTML bundle (html + assets, no manifest) — "static page".
  const staticHtml = insp.signals.find((s) => s.marker === 'static-html-bundle');
  if (staticHtml) {
    const signals = [...insp.signals, ...genericSignal];
    return {
      kind: 'inspiration',
      confidence: 'medium',
      signals,
      suggested_slug,
      suggested_name,
      inspiration_subtype: insp.subtype ?? 'doc',
    };
  }

  // 4 — Unknown: surface the two most likely kinds for the forced question.
  const appWeight = maxWeight(app.signals);
  const inspWeight = maxWeight(insp.signals);
  const ambiguous_options = buildAmbiguousOptions(appWeight, inspWeight);
  return {
    kind: 'unknown',
    confidence: 'low',
    signals: [...app.signals, ...insp.signals, ...genericSignal],
    suggested_slug,
    suggested_name,
    ambiguous_options,
  };
}

/**
 * The forced disambiguation is always app-vs-inspiration (spec §B.5). Order by
 * whatever weak signals exist; fall back to an even split when nothing leans.
 */
function buildAmbiguousOptions(
  appWeight: number,
  inspWeight: number,
): Array<{ kind: DetectedKind; weight: number }> {
  const a = appWeight > 0 ? appWeight : 0.5;
  const i = inspWeight > 0 ? inspWeight : 0.5;
  const options: Array<{ kind: DetectedKind; weight: number }> = [
    { kind: 'app', weight: a },
    { kind: 'inspiration', weight: i },
  ];
  options.sort((x, y) => y.weight - x.weight);
  return options;
}
